'use client'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'

import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { useStatsigNewCLPFilterExperiment } from '@src/pages/[...slug]/components/collection/hooks/useStatsigNewCLPFilterExperiment'
import { LoaderWrapper } from '@src/quince-components/src'
import {
  FilterSectionWithDrawer,
  FilterSectionWithDrawerProps,
  FilterWithSidebar,
  FilterWithSidebarItemCountAndSort,
  FilterWithSidebarProps,
  MobileFilters,
  UpdateQueryParams,
} from '@src/quince-components/src/atoms/filterSection/menu'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'

import { useSearchResults } from './hooks/useSearchResults'
import { SearchResults } from './searchResults/SearchResults'
import searchResultsStyles from './searchResults/searchResults.module.scss'
import styles from './searchResultsSection.module.scss'

interface ISearchResultsSection {
  setSearchLoaded: Dispatch<SetStateAction<boolean>>
}

function SearchResultsSection({ setSearchLoaded }: ISearchResultsSection) {
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const {
    productCards,
    isLayoutLoading,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts,
    filters,
    selectedFilters,
    updateQuery,
    hideSearchHeader,
  } = useSearchResults(isRecurringItemSubscriptionsExpEnabled)

  useEffect(() => {
    setSearchLoaded(!isLayoutLoading)
  }, [isLayoutLoading, setSearchLoaded])

  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const { showDesktopFilterWithSidebar, showDesktopFilterWithDrawer } = useStatsigNewCLPFilterExperiment()
  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  const shouldRenderFilters = totalProducts > 0 || hasAppliedFilters

  const handleResetFilters = useCallback(() => {
    updateQuery({ filter: { type: 'reset' } })
  }, [updateQuery])

  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <SomethingWentWrong wrapperClass={styles.somethingWentWrongWrapper} minHeight={'20rem'} />
  }

  function getFilterSectionComponent(): React.ComponentType<FilterSectionWithDrawerProps | FilterWithSidebarProps> {
    if (isDesktop) {
      if (showDesktopFilterWithDrawer) {
        return FilterSectionWithDrawer
      }
      if (showDesktopFilterWithSidebar) {
        return FilterWithSidebar
      }
      return FilterSectionWithDrawer
    }

    return MobileFilters as React.ComponentType<FilterSectionWithDrawerProps>
  }

  function renderFilterSection(Component: React.ComponentType<FilterSectionWithDrawerProps | FilterWithSidebarProps>) {
    const baseProps = {
      noOfItems: totalProducts,
      filters: filters,
      sortBy: '',
      selectedFilters: selectedFilters as unknown as Record<string, string[]>,
      updateQuery: updateQuery as (params: UpdateQueryParams) => void,
      showSelectedOption: true,
      shouldRenderColorFilterHtml: false,
      skipSorting: true,
      className: classNames(styles.searchResultsFilterSection, {
        [styles.searchResultsFilterSection__withHiddenHeader ?? '']: hideSearchHeader,
      }),
    }

    return <Component {...baseProps} />
  }

  return (
    <div>
      <LoaderWrapper showLoader={isLayoutLoading} minHeight={`${searchResultsStyles.searchResultsLoaderMinHeight}rem`}>
        {searchQuery && !hideSearchHeader && totalProducts > 0 ? (
          <div
            className={`${styles.newCLPFiltersSearchTitleContainer} ${
              showDesktopFilterWithSidebar && !showDesktopFilterWithDrawer
                ? styles.newCLPFiltersSearchTitleContainer__withSidebar
                : ''
            }`}
          >
            <h1
              className={`${styles.newCLPFiltersSearchTitle} ${
                showDesktopFilterWithDrawer ? styles.newCLPFiltersDrawerSearchTitle : ''
              }`}
            >{`"${searchQuery.toUpperCase()}"`}</h1>
            <p className={styles.newCLPFiltersProductCountText}>
              {`${t('showing')} ${t(totalProducts === 1 ? 'totalitems_item' : 'totalitems_items', {
                totalItems: totalProducts,
              })}`}
            </p>
          </div>
        ) : null}
        {showDesktopFilterWithDrawer && filters.length && shouldRenderFilters
          ? renderFilterSection(getFilterSectionComponent())
          : null}
        <div
          id={'newSearchSection'}
          className={`${
            showDesktopFilterWithSidebar && !showDesktopFilterWithDrawer && totalProducts > 0
              ? styles.searchResultsSectionNewCLPFiltersWithSidebar
              : ''
          }`}
        >
          {!showDesktopFilterWithDrawer && filters.length && shouldRenderFilters
            ? renderFilterSection(getFilterSectionComponent())
            : null}
          <div
            className={classNames(!totalProducts ? styles.noResultsContainer : '', {
              [`${styles.noResultsWithoutFilter}`]: !Object.keys(filters).length,
              [`${styles.noResultsContainer__newCLPFiltersDesktopWithSidebar}`]:
                showDesktopFilterWithSidebar && !showDesktopFilterWithDrawer && totalProducts > 0,
            })}
          >
            {showDesktopFilterWithSidebar && !showDesktopFilterWithDrawer && totalProducts ? (
              <FilterWithSidebarItemCountAndSort
                noOfItems={totalProducts}
                sortBy=""
                updateQuery={updateQuery as (params: UpdateQueryParams) => void}
                skipSorting
              />
            ) : null}
            <SearchResults
              searchQuery={searchQuery}
              productCards={productCards}
              isLayoutLoading={isLayoutLoading}
              intersectRef={intersectRef}
              isMoreProductsLoading={isMoreProductsLoading}
              totalProducts={totalProducts}
              onResetFilters={handleResetFilters}
              selectedFilters={selectedFilters}
              showDesktopFilterWithSidebar={showDesktopFilterWithSidebar && !showDesktopFilterWithDrawer}
            />
          </div>
        </div>
      </LoaderWrapper>
    </div>
  )
}

export default SearchResultsSection
